import type { ReactNode } from 'react'

import { cn } from '@/lib/cn'
import { formatNumber } from '@/lib/format'

export interface ChartDatum {
  key: string
  label: string
  value: number
}

export interface ChartFrameProps {
  title: string
  subtitle?: string
  data: ChartDatum[]
  valueHeading?: string
  categoryHeading?: string
  children: ReactNode
  className?: string
  actions?: ReactNode
}

/**
 * Shared chart shell.
 *
 * Carries the title, and, for accessibility, the same numbers as a real table
 * that screen readers reach and sighted users can expand. Nothing a chart shows
 * is available only as a picture.
 */
export function ChartFrame({
  title,
  subtitle,
  data,
  valueHeading = 'Records',
  categoryHeading = 'Category',
  children,
  className,
  actions,
}: ChartFrameProps) {
  return (
    <figure className={cn('flex min-w-0 flex-1 flex-col', className)}>
      <figcaption className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-ink">{title}</h3>
          {subtitle ? (
            <p className="mt-0.5 text-xs text-ink-secondary">{subtitle}</p>
          ) : null}
        </div>
        {actions}
      </figcaption>

      {children}

      <details className="group mt-3 pt-1 max-lg:mt-3 lg:mt-auto">
        <summary className="w-fit cursor-pointer text-xs text-ink-muted transition-colors hover:text-ink-secondary">
          <span className="group-open:hidden">Show as table</span>
          <span className="hidden group-open:inline">Hide table</span>
        </summary>
        <div className="scrollbar-slim mt-2 max-h-56 overflow-y-auto rounded-md border border-line">
          <table className="w-full text-xs">
            <caption className="sr-only">{title}</caption>
            <thead className="sticky top-0 bg-surface-sunken">
              <tr>
                <th scope="col" className="px-2.5 py-1.5 text-left font-medium text-ink-secondary">
                  {categoryHeading}
                </th>
                <th scope="col" className="px-2.5 py-1.5 text-right font-medium text-ink-secondary">
                  {valueHeading}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((datum) => (
                <tr key={datum.key} className="border-t border-line">
                  <th scope="row" className="px-2.5 py-1.5 text-left font-normal text-ink">
                    {datum.label}
                  </th>
                  <td className="tabular px-2.5 py-1.5 text-right text-ink-secondary">
                    {formatNumber(datum.value)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </figure>
  )
}
